/*
 * An authenticated conversation or first-message view inside the application's popup layer.
 * App opens this element for a conversation identifier or a selected recipient.
 * Authentication changes replace the authorized content, and closing releases the popup's
 * descendants.
 *
 * Example: <ConversationPopup context={context} conversationId={id} onClose={close}/>
 */

import { UIElement, defineUIElements } from '../../runtime/components/ui-element.js';
import { servicesContext } from '../../runtime/services.js';
import { isAuthCancelled } from '../../runtime/auth.js';
import { registerComponents as registerConversation, ConversationScreen } from './conversation.jsx';

export class ConversationPopup extends UIElement {
  static properties = {
    conversationId: { attribute: false },
    recipientId: { attribute: false },
    onClose: { attribute: false },
    setInterests: { attribute: false },
    authorized: { state: true },
    error: { state: true }
  };

  constructor() {
    super();
    this.authorized = false;
    this.error = '';
    this.captureScreen = (node) => (this.screen = node);
    this.bindMethods('close', 'keydown', 'route');
    this.addEventListener('keydown', this.keydown);
    this.own(() => this.removeEventListener('keydown', this.keydown));
    this.own(() => this.releaseWorkflow?.());
  }

  willUpdate(changed) {
    super.willUpdate(changed);
    servicesContext(this.context, ['auth', 'a11y']);
  }

  updated(changed) {
    super.updated(changed);
    if (!this.connection) return;
    this.route();
    if (!this.focusedInitially) {
      this.focusedInitially = true;
      if (!this.context.services.a11y.hasOpenModal()) this.context.services.a11y.focusElement(this);
    }
    void this.initialize();
  }

  route() {
    const auth = this.context.services.auth;
    const owner = auth.getAuthOwner();
    if (this.releaseWorkflow && this.workflowOwner === owner) return;
    this.releaseWorkflow?.();
    this.workflowOwner = owner;
    this.releaseWorkflow = auth.registerAuthWorkflow();
  }

  initialize() {
    return (this.initialization ||= this.ensure('conversation', async () => {
      this.route();
      await this.context.services.auth.requireAuthentication({ signal: this.signal });
      if (this.disposed) return;
      this.authorized = true;
      await this.updateComplete;
      if (this.disposed) return;
      await this.screen.initialize();
      if (this.disposed) return;
      this.afterRender(() => {
        const { a11y } = this.context.services;
        if (!a11y.hasOpenModal() && this.ownerDocument.activeElement === this)
          a11y.focusElement(
            this.screen.querySelector('h1, h2, [contenteditable="true"], textarea, button') ||
              this.screen
          );
      }, 'conversation-focus');
      await this.whenRendered();
      return this;
    }).catch(
      this.guard((error) => {
        if (isAuthCancelled(error)) return this.close();
        this.error = error.message;
        this.authorized = false;
      })
    ));
  }

  close() {
    const close = this.onClose;
    return close?.();
  }

  keydown(event) {
    const { a11y } = this.context.services;
    if (
      event.key !== 'Escape' ||
      event.defaultPrevented ||
      event.isComposing ||
      a11y.hasOpenModal() ||
      event.ctrlKey ||
      event.metaKey ||
      event.altKey ||
      event.shiftKey
    )
      return;
    queueMicrotask(
      this.guard(() => {
        if (!event.defaultPrevented && !a11y.hasOpenModal()) this.close();
      })
    );
  }

  render() {
    return this.authorized ? (
      <ConversationScreen
        ref={this.captureScreen}
        class="conversation-view view-chat"
        context={this.context}
        conversationId={this.conversationId}
        recipientId={this.recipientId}
        popup
        onClose={this.close}
        setInterests={this.setInterests}
        active={this.active}/>
    ) : (
      <div class="conversation-popup-body">
        {this.error ? <div class="alert-error">{this.error}</div> : null}
      </div>
    );
  }
}

export function registerConversationPopup(registry) {
  registerConversation(registry);
  defineUIElements(registry, { 'tbc-conversation-popup': ConversationPopup });
}
